import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/esm/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useRef, useEffect } from 'react';
import { TextInput } from '@paypalcorp/pp-react-text-input';
import { V2CriticalIcon } from '@paypalcorp/pp-react-icons';
import clsx from 'clsx';
import '../__generated__/styles/code-input.css';

var styles = {"ppvx_code-input":"ppvx_code-input___1-4-7","ppvx_code-input__text-input":"ppvx_code-input__text-input___1-4-7","ppvx_code-input__field--error":"ppvx_code-input__field--error___1-4-7","ppvx_code-input__input":"ppvx_code-input__input___1-4-7","ppvx_code-input__input-wrapper":"ppvx_code-input__input-wrapper___1-4-7","ppvx_code-input__error":"ppvx_code-input__error___1-4-7","ppvx_code-input__error-text":"ppvx_code-input__error-text___1-4-7","ppvx_code-input__error-icon":"ppvx_code-input__error-icon___1-4-7","ppvx_code-input__error-text--list":"ppvx_code-input__error-text--list___1-4-7"};

var _excluded = ["alphaCharCapitalize", "className", "errorText", "error", "name", "id", "inputClassName", "inputWrapperClassName", "onChange", "pinLength", "value", "autoFocus", "onBlur", "onFocus", "numberOnly"];
var CodeInput = React.forwardRef(function (_ref, ref) {
  var _ref$alphaCharCapital = _ref.alphaCharCapitalize,
      alphaCharCapitalize = _ref$alphaCharCapital === void 0 ? false : _ref$alphaCharCapital,
      className = _ref.className,
      errorText = _ref.errorText,
      _ref$error = _ref.error,
      error = _ref$error === void 0 ? !!errorText : _ref$error,
      name = _ref.name,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? name : _ref$id,
      inputClassName = _ref.inputClassName,
      inputWrapperClassName = _ref.inputWrapperClassName,
      onChange = _ref.onChange,
      _ref$pinLength = _ref.pinLength,
      pinLength = _ref$pinLength === void 0 ? 6 : _ref$pinLength,
      value = _ref.value,
      autoFocus = _ref.autoFocus,
      _ref$onBlur = _ref.onBlur,
      onBlur = _ref$onBlur === void 0 ? null : _ref$onBlur,
      _ref$onFocus = _ref.onFocus,
      onFocus = _ref$onFocus === void 0 ? null : _ref$onFocus,
      _ref$numberOnly = _ref.numberOnly,
      numberOnly = _ref$numberOnly === void 0 ? false : _ref$numberOnly,
      rest = _objectWithoutProperties(_ref, _excluded);

  {
    var internalRef = useRef([]);
    var inputFieldRef = ref || internalRef; // pin length 3-6

    var MAX_PIN = 6; // key codes

    var NUM_REGEX = /^[0-9]+$/;
    var keyEventsArry = ['Backspace', 'Delete', 'Tab'];

    var _React$useState = React.useState(false),
        _React$useState2 = _slicedToArray(_React$useState, 2),
        focused = _React$useState2[0],
        setFocused = _React$useState2[1];

    var pinArray = []; // check pin length and convert into array

    if (pinLength < 3 || pinLength >= MAX_PIN) {
      pinArray = _toConsumableArray(Array(MAX_PIN).keys());
    } else {
      pinArray = _toConsumableArray(Array(pinLength).keys());
    } // convert it into an array and remove empty value


    var valueArr = value && value.split('').map(function (val) {
      if (val === ' ') {
        return '';
      }

      return val;
    }); //  generate the final code

    var generateCode = function () {
      // generate the final code and add spaces for empty value
      var pinArr = inputFieldRef.current.map(function (input) {
        if (input.value === '') {
          return ' ';
        }

        return input.value.slice(0, 1);
      }).join(''); // onChange is called on each key

      if (onChange) {
        onChange(pinArr);
      }
    }; // focus on field provided an index valid


    var focusOnField = function (idx) {
      // check if idx is in the range

      /* istanbul ignore next */
      if (inputFieldRef && inputFieldRef.current[idx]) {
        inputFieldRef.current[idx].focus();
      }
    }; // process each character paste/input in each field


    var iterateEachCharacter = function (pastedArr, idx) {
      var fieldLength = inputFieldRef.current.length;
      var minArr = Math.min(pastedArr && pastedArr.length, fieldLength);
      var index = idx;

      for (var i = 0; i < minArr; i++) {
        inputFieldRef.current[index].value = alphaCharCapitalize ? pastedArr[i].toUpperCase() : pastedArr[i];
        index++; // break the loop if index is equal to length of the field

        if (index === fieldLength) {
          break;
        }
      } // focus on next field or focus on the last field


      focusOnField(index === fieldLength ? index - 1 : index);
    };

    var handleInput = function (evt, idx) {
      // move focus fwd if there is a value
      if (evt.target.value !== '') {
        focusOnField(idx + 1);
      } // capitalize the characters


      if (alphaCharCapitalize) {
        evt.target.value = evt.target.value.toUpperCase();
      } // if more than 1 characters split the values
      // iterate each character into single field.


      if (evt.target.value.length > 1) {
        var pastedArr = evt.target.value.replace(/\s/g, '').split('');
        iterateEachCharacter(pastedArr, idx);
      }

      generateCode();
    };

    useEffect(function () {
      if (autoFocus) {
        focusOnField(0);
      }
    }, []);
    /* eslint-disable complexity */

    var handleKeyDown = function (evt, idx) {
      /* istanbul ignore next */
      var keyCode = evt.key || evt.keyCode;
      /* istanbul ignore next */

      switch (keyCode) {
        case 27:
        case 'Backspace':
        case 46:
        case 'Delete':
          if (evt.target.value === '' || evt.target.value === ' ') {
            focusOnField(idx - 1);
          } else {
            evt.target.value = ' ';
            generateCode();
          }

          break;

        case 32:
        case ' ':
          // remove spacebar
          evt.preventDefault();
          break;

        case 37:
        case 'ArrowLeft':
          evt.preventDefault(); // move focus back

          focusOnField(idx - 1);
          break;

        case 39:
        case 'ArrowRight':
          evt.preventDefault(); // move focus forward

          focusOnField(idx + 1);
          break;

        case 38:
        case 'ArrowUp':
        case 40:
        case 'ArrowDown':
          evt.preventDefault();
          break;

        case 36:
        case 'Home':
          evt.preventDefault(); // move focus back

          focusOnField(0);
          break;

        case 35:
        case 'End':
          evt.preventDefault(); // move focus to last

          focusOnField(pinLength - 1);
          break;

        default:
          // select the value so it can be replaced
          evt.target.select();
          break;
      } // to allow paste command ctrl + v / cmd + v


      if ((evt.ctrlKey || evt.metaKey) && keyCode === 'v') {
        return;
      } // limit users to allow only numeric value


      if (!keyCode.match(NUM_REGEX) && !keyEventsArry.includes(keyCode) && numberOnly) {
        evt.preventDefault();
      }
    };
    /* eslint-enable complexity */
    // set ref of each input field and manually add the value
    // to make it a controlled component


    var setRef = function (el, idx) {
      if (el) {
        inputFieldRef.current[idx] = el;
        inputFieldRef.current[idx].value = valueArr ? valueArr[idx] : '';
      }
    };

    var handleBlur = function (event) {
      /* istanbul ignore else */
      if (onBlur && !inputFieldRef.current.includes(event.relatedTarget)) {
        onBlur(event);
        setFocused(false);
      }
    };

    var handleFocus = function (event) {
      /* istanbul ignore else */
      event.target.select();

      if (onFocus && !focused) {
        setFocused(true);
        onFocus(event);
      }
    }; // handle paste


    var handlePaste = function (evt, idx) {
      var clipboardValue = evt.clipboardData && evt.clipboardData.getData('text'); // ignore the default behavior, onPaste calls onChange

      evt.preventDefault();

      if (clipboardValue && !clipboardValue.match(NUM_REGEX) && numberOnly) {
        return;
      }

      var pastedArr = clipboardValue && clipboardValue.replace(/\s/g, '').split('');
      iterateEachCharacter(pastedArr, idx);
      generateCode();
    };

    var containerClasses = clsx(styles['ppvx_code-input'], className);
    var textInputContainerClasses = clsx(styles['ppvx_code-input__text-input'], _defineProperty({}, styles['ppvx_code-input__field--error'], errorText), inputWrapperClassName);
    var inputClasses = clsx(styles['ppvx_code-input__input'], inputClassName);
    var loadInputField = pinArray.map(function (idx) {
      return /*#__PURE__*/React.createElement(TextInput, _extends({
        key: idx,
        className: textInputContainerClasses,
        inputClassName: inputClasses,
        "aria-label": "".concat(idx + 1, "-").concat(pinArray.length),
        role: "textbox",
        name: "".concat(name, "-").concat(idx),
        id: "ci-".concat(id, "-").concat(idx),
        ref: function (el) {
          return setRef(el, idx);
        },
        onInput: function onInput(evt) {
          return handleInput(evt, idx);
        },
        onKeyDown: function onKeyDown(evt) {
          return handleKeyDown(evt, idx);
        },
        onPaste: function onPaste(evt) {
          return handlePaste(evt, idx);
        },
        onFocus: handleFocus,
        onBlur: handleBlur,
        theme: "v2",
        "aria-describedby": error ? "message_".concat(name) : null,
        error: error && !!errorText,
        pattern: numberOnly ? '[0-9]*' : ''
      }, rest));
    });
    return /*#__PURE__*/React.createElement("div", {
      className: containerClasses,
      id: id
    }, /*#__PURE__*/React.createElement("div", {
      className: styles['ppvx_code-input__input-wrapper']
    }, loadInputField), errorText && function loadError() {
      var processText = Array.isArray(errorText) ? /*#__PURE__*/React.createElement("ul", {
        className: styles['ppvx_code-input__error-text--list']
      }, errorText.map(function (text, index) {
        return /*#__PURE__*/React.createElement("li", {
          key: "text$-".concat(index)
        }, text);
      })) : errorText;
      return /*#__PURE__*/React.createElement("div", {
        className: clsx(styles['ppvx_code-input__error']),
        id: "message_".concat(name),
        role: "alert"
      }, /*#__PURE__*/React.createElement(V2CriticalIcon, {
        className: styles['ppvx_code-input__error-icon'],
        size: "sm",
        "aria-hidden": "true"
      }), /*#__PURE__*/React.createElement("span", {
        className: styles['ppvx_code-input__error-text']
      }, processText));
    }());
  }
});

export { CodeInput };
